import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ArrowLeft, History, Plus, Save } from 'lucide-react';
import {
  deleteCommand,
  getCommand,
  getCommandVersions,
  listCommands,
  saveCommand,
  type CommandResponse,
  type CommandSummary,
} from '@/lib/api';
import { ResourceDeleteConfirm } from '@/components/common/ResourceDeleteConfirm';

interface CommandsPageProps {
  routePrefix?: string;
}

export function CommandsPage({
  routePrefix = '/admin/resources/commands',
}: CommandsPageProps): React.ReactElement {
  const { name } = useParams<{ name?: string }>();
  return name ? (
    <CommandEditor name={name} routePrefix={routePrefix} />
  ) : (
    <CommandListView routePrefix={routePrefix} />
  );
}

function CommandListView({ routePrefix }: { routePrefix: string }): React.ReactElement {
  const [commands, setCommands] = useState<CommandSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listCommands()
      .then(setCommands)
      .catch((err: Error) => {
        setError(err.message);
      });
  }, []);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <h1 className="text-lg font-semibold text-text-primary">Commands</h1>
        <Link
          to={`${routePrefix}/new`}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90"
        >
          <Plus className="size-4" />
          New Command
        </Link>
      </div>
      <div className="flex-1 overflow-auto px-4 pb-4 pt-2">
        {error && <div className="rounded-md bg-error/15 p-3 text-sm text-error">{error}</div>}
        {!commands && !error && <div className="text-sm text-text-tertiary">Loading…</div>}
        {commands?.length === 0 && (
          <div className="text-sm text-text-tertiary">No commands yet.</div>
        )}
        {commands && commands.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-text-secondary">
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Source</th>
              </tr>
            </thead>
            <tbody>
              {commands.map(command => (
                <tr key={command.name} className="border-b border-border hover:bg-surface/50">
                  <td className="py-2 pr-4">
                    <Link
                      to={`${routePrefix}/${encodeURIComponent(command.name)}`}
                      className="text-primary"
                    >
                      {command.name}
                    </Link>
                  </td>
                  <td className="py-2 pr-4">
                    <span className="rounded bg-surface-elevated px-2 py-0.5 text-xs">
                      {command.source}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

interface VersionRow {
  version: number;
  savedAt: string;
}

function CommandEditor({
  name,
  routePrefix,
}: {
  name: string;
  routePrefix: string;
}): React.ReactElement {
  const navigate = useNavigate();
  const isNew = name === 'new';
  const [command, setCommand] = useState<CommandResponse | null>(null);
  const [commandName, setCommandName] = useState(isNew ? '' : name);
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [versions, setVersions] = useState<VersionRow[]>([]);
  const [showVersions, setShowVersions] = useState(false);

  useEffect(() => {
    if (isNew) {
      return;
    }

    getCommand(name)
      .then(result => {
        setCommand(result);
        setBody(result.body);
      })
      .catch((err: Error) => {
        setError(err.message);
      });
  }, [isNew, name]);

  const isBundled = command?.source === 'bundled';

  async function handleSave(): Promise<void> {
    if (!commandName) {
      setError('Name is required');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const saved = await saveCommand(commandName, body);
      setCommand(saved);
      if (isNew) {
        navigate(`${routePrefix}/${encodeURIComponent(commandName)}`);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(): Promise<void> {
    try {
      await deleteCommand(commandName);
      navigate(routePrefix);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function handleShowVersions(): Promise<void> {
    try {
      const result = await getCommandVersions(commandName);
      setVersions(
        result.versions.map(version => ({
          version: version.version,
          savedAt: version.savedAt,
        }))
      );
      setShowVersions(true);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex items-center gap-2 border-b border-border px-4 py-2">
        <Link to={routePrefix} className="text-text-secondary hover:text-text-primary">
          <ArrowLeft className="size-4" />
        </Link>
        <input
          className="flex-1 rounded bg-surface px-2 py-1 text-sm"
          value={commandName}
          onChange={event => {
            setCommandName(event.target.value);
          }}
          placeholder="command-name"
          disabled={!isNew}
        />
        <button
          onClick={handleSave}
          disabled={saving || isBundled}
          className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1 text-sm text-white disabled:opacity-50"
        >
          <Save className="size-4" /> Save
        </button>
        {!isNew && (
          <>
            <button
              onClick={handleShowVersions}
              className="inline-flex items-center gap-1 rounded-md bg-surface-elevated px-3 py-1 text-sm"
            >
              <History className="size-4" /> History
            </button>
            <ResourceDeleteConfirm
              resourceKind="command"
              resourceName={commandName}
              disabled={isBundled}
              onConfirm={handleDelete}
            />
          </>
        )}
      </div>
      {error && (
        <div className="border-b border-error/30 bg-error/15 px-4 py-2 text-sm text-error">
          {error}
        </div>
      )}
      <div className="flex flex-1 overflow-hidden">
        <textarea
          className="flex-1 resize-none bg-background p-4 font-mono text-sm text-text-primary outline-none"
          value={body}
          onChange={event => {
            setBody(event.target.value);
          }}
          readOnly={isBundled}
          placeholder="# Command instructions…"
        />
        {showVersions && (
          <div className="w-72 overflow-auto border-l border-border bg-background p-2 text-sm">
            <div className="mb-2 flex items-center justify-between">
              <span className="font-semibold">Versions</span>
              <button
                onClick={() => {
                  setShowVersions(false);
                }}
                className="text-xs text-text-secondary"
              >
                close
              </button>
            </div>
            {versions.length === 0 && (
              <div className="text-text-tertiary">No previous versions</div>
            )}
            {versions.map(version => (
              <div key={version.version} className="mb-1 rounded border border-border p-2 text-xs">
                <div>v{version.version}</div>
                <div className="text-text-tertiary">{version.savedAt.slice(0, 19)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
